import React, { useEffect, useState } from 'react';
import StageCompletionStyles from '../styles/stageCompletion';
import { AuthPageStyles } from '../../authentication/styles';
import { Box, Button, Grid, Typography } from '@mui/material';
import { ReactComponent as StageCompletion } from '../images/stageCompletion.svg';
import { ReactComponent as AllTaskDone } from '../images/allTasksDone.svg';
import { ReactComponent as StageUnlocked } from '../images/stageUnlocked.svg';
import { ReactComponent as FormsComplete } from '../images/formsComplete.svg';
import { ReactComponent as EvaluationLoader } from '../images/evaluationLoader.svg';
import { BrandColors, routeMappings, WorkflowItemCode } from '../../constants';
import { STAGE_COMPLETION_MESSAGE_TIME } from '../../constants/timers';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CandidateMessages, getCandidateMessages, psuedoResponse } from '../../common/utils/getCandidateMessages';
import { FullPageLoader } from '../../common/components';
import ReactHtmlParser from 'html-react-parser';
import { useWorkflowEndConfig } from '../../taskList/hooks';
import LinkRedirectionTimerSection from '../../taskList/components/LinkRedirectionTimerSection';
import { TemplateType } from '../../store/interfaces';
import { ASSESSMENT_EVALUATION_POLLING_EXPIRE_TIME, INTEGRATED_LINK_REDIRECTION_TIME } from '../../taskList/constants';
import { useAppSelector } from '../../store/hook';
import { StaticPageIconStyles } from '../../common/styles/staticPageIcon';
import PaperContainer from '../../authSimplified/components/PaperContainer';
import { isJourneyTheme } from '../../common/utils/fallbackCondition';
import { usePrefersReducedAnimation } from '../../common/hooks/usePrefersReducedAnimation';

type StaticPageProps = {
  icon: object;
  pageHeading: string;
  pageMessage: string;
  buttonText?: string;
  evaluationIndicator?: boolean;
};
interface StageSectionProps {
  [key: string]: StaticPageProps;
}

export const StageCompletionPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams<{ id?: string; itemId?: string }>();
  const template = useAppSelector((state) => state.template);
  const { isICPEnabled } = useAppSelector((state) => state.headerDetails);
  const { classes } = AuthPageStyles({ isICPEnabled: isICPEnabled });
  const { classes: stageCompletionClasses } = StageCompletionStyles();
  const { classes: StaticPageIconClasses } = StaticPageIconStyles();
  const [loading, setLoading] = useState<boolean>(true);
  const [messageData, setMessageData] = useState<CandidateMessages>(psuedoResponse.data);
  let isBasicFlow = false;
  const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
  if (currentWorkflowItem) isBasicFlow = currentWorkflowItem.code === WorkflowItemCode.BASICTASKLIST;

  const workflowEndConfig = useWorkflowEndConfig();

  const dataObj: StageSectionProps = {
    [routeMappings['FORM_COMPLETION']]: {
      icon: <FormsComplete width="120px" height="120px" className={StaticPageIconClasses.staticPageIcon} />,
      pageHeading: t('formCompleteHeading'),
      pageMessage: t('formCompleteMessage'),
      buttonText: t('formCompleteNext'),
    },
    [routeMappings['TASK_COMPLETION']]: {
      icon: <StageUnlocked width="120px" height="120px" className={StaticPageIconClasses.staticPageIcon} />,
      pageHeading: t('stageCompleteHeading'),
      pageMessage: t('stageCompleteMessage'),
      buttonText: t('formCompleteNext'),
    },
    [routeMappings['BASICTASKLIST_END']]: {
      icon: <AllTaskDone width="120px" height="120px" className={StaticPageIconClasses.staticPageIcon} />,
      pageHeading: messageData.endPageHeading,
      pageMessage: messageData.endPageContent,
    },
    [routeMappings['BASICTASKLIST_HURDLE_FAIL']]: {
      icon: <StageCompletion width="120px" height="120px" className={StaticPageIconClasses.staticPageIcon} />,
      pageHeading: messageData.hurdleFailHeading,
      pageMessage: messageData.hurdleFailContent,
    },
    [routeMappings[`${currentWorkflowItem.code}_MANUAL_HURDLE`]]: {
      icon: <StageCompletion width="120px" height="120px" className={StaticPageIconClasses.staticPageIcon} />,
      pageHeading: messageData.manualHurdleHeading,
      pageMessage: messageData.manualHurdleContent,
    },
    [`${routeMappings['TASK']}/${routeParams.id}${routeMappings['EXPASSES']}/${routeParams.itemId}${routeMappings['SCORE_EVALUATION']}`]:
      {
        icon: <EvaluationLoader width="100px" height="100px" className={StaticPageIconClasses.staticPageIcon} />,
        pageHeading: t('basicPollingHeading'),
        pageMessage: t('basicPollingContent'),
        evaluationIndicator: true,
      },
  };

  const redirectToTaskList = (): void => {
    navigate(`${routeMappings['BASICTASKLIST']}/:id`);
  };

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        const responseData = await getCandidateMessages();
        setMessageData({
          ...responseData.data,
          manualHurdleContent: responseData.data.manualHurdleContent || t('manualHurdleContent'),
          manualHurdleHeading: responseData.data.manualHurdleHeading || t('manualHurdleHeading'),
          hurdleFailHeading: responseData.data.hurdleFailHeading || t('manualHurdleHeading'),
          hurdleFailContent: responseData.data.hurdleFailContent || t('manualHurdleContent'),
          endPageHeading: responseData.data.endPageHeading || t('endCompleteHeading'),
          endPageContent: responseData.data.endPageContent || t('endCompleteMessage'),
        });
      } catch (e) {
        setMessageData({
          welcomePageHeading: '',
          welcomePageContent: '',
          taskListPageContent: '',
          manualHurdleContent: t('manualHurdleContent'),
          manualHurdleHeading: t('manualHurdleHeading'),
          hurdleFailHeading: t('manualHurdleHeading'),
          hurdleFailContent: t('manualHurdleContent'),
          endPageHeading: t('endCompleteHeading'),
          endPageContent: t('endCompleteMessage'),
          taskListPageHeading: '',
        });
        console.error('error while fetching candidate messages', e);
      }
      setLoading(false);
    })();

    const redirectionTimeout = setTimeout(() => {
      if (dataObj[location.pathname].buttonText) {
        redirectToTaskList();
      }
    }, STAGE_COMPLETION_MESSAGE_TIME);
    return (): void => {
      clearTimeout(redirectionTimeout);
    };
  }, []);

  useEffect(() => {
    const redirectToManual = setTimeout(() => {
      if (dataObj[location.pathname].evaluationIndicator) {
        navigate(routeMappings[`${currentWorkflowItem.code}_MANUAL_HURDLE`]);
      }
    }, ASSESSMENT_EVALUATION_POLLING_EXPIRE_TIME);

    return (): void => {
      clearTimeout(redirectToManual);
    };
  }, []);

  /**
   * STOP OR START animation as per system configurations of animation effects
   */
  usePrefersReducedAnimation();

  return (
    <>
      <FullPageLoader isLoading={loading} />
      <PaperContainer variant="medium">
        <Box py={4} className={isJourneyTheme(template.layoutCode) ? stageCompletionClasses.journeyContainer : ''}>
          <Box textAlign="center">
            <Grid container justifyContent="center" className={classes.container}>
              <Grid item xl={8} lg={7}>
                <Box className={stageCompletionClasses.stageCompletionHeading}>
                  {isBasicFlow || location.pathname.indexOf(routeMappings['SCORE_EVALUATION']) !== -1 ? (
                    (dataObj[location.pathname].icon as React.JSX.Element)
                  ) : (
                    <></>
                  )}
                  <Typography variant="h1">{dataObj[location.pathname].pageHeading}</Typography>
                </Box>
                <Box className={stageCompletionClasses.stageCompletionText}>
                  <Box textAlign="center">{ReactHtmlParser(dataObj[location.pathname].pageMessage)}</Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          {dataObj[location.pathname].buttonText && (
            <Grid item xs={12}>
              <Box width="100%" textAlign="center">
                <Button
                  type="submit"
                  variant="outlined"
                  color="primary"
                  aria-disabled="false"
                  className={stageCompletionClasses.staticPageButtom}
                  onClick={redirectToTaskList}>
                  <div>{dataObj[location.pathname].buttonText}</div>
                </Button>
              </Box>
            </Grid>
          )}

          {(location.pathname === routeMappings['BASICTASKLIST_END'] ||
            location.pathname === routeMappings['BASICTASKLIST_HURDLE_FAIL']) &&
          workflowEndConfig.assessmentCompletionURL ? (
            <Grid container justifyContent="center">
              <LinkRedirectionTimerSection
                basicFlow={template.type === TemplateType.BASIC_EXPERIENCE_LITE}
                bannerImageEnabled={false}
                nextStepLabelColor={BrandColors.grey100}
                content={messageData.endPageHeading}
                redirectUrl={workflowEndConfig.assessmentCompletionURL}
                time={INTEGRATED_LINK_REDIRECTION_TIME}
              />
            </Grid>
          ) : (
            <></>
          )}
        </Box>
      </PaperContainer>
    </>
  );
};
